import cn from 'classnames'

import type { FC } from 'react'
import type { Price } from '@/root/shared-types/dto/SailPricing'
import { noop } from 'lodash'
import { useAppConfig, useGetTranslations } from '@/root/shared-hooks'
import { AddToCartIcon, PenIcon } from '@/root/ui/shared'
import { getIcon } from '@/root/modules/shared/SelectTicketPage'

interface PriceCardProps {
  price: number
  title: string
  priceSource?: Price
  quantity?: number
  disabled?: boolean
  handleClick?: (price: Price) => void
  handleInc?: () => void
  handleDec?: () => void
  handleRemove?: () => void
  withQuantity?: boolean
  oneLineInfo?: string
}

export const PriceCardDeprecated: FC<PriceCardProps> = ({
  price,
  title,
  quantity,
  handleClick = noop,
  disabled = false,
  priceSource,
  handleRemove,
  withQuantity = false,
  handleInc,
  handleDec,
  oneLineInfo,
}) => {
  let clickHandler = priceSource ? () => handleClick(priceSource) : handleClick
  if (disabled) {
    clickHandler = noop
  }

  const handleCardClick = () => {
    if (disabled) return
    clickHandler?.()
  }

  const { formatMoney } = useAppConfig()
  const { getTranslation } = useGetTranslations()

  const code = priceSource ? priceSource.priceCategory.code : ''

  // Todo: clarify business rules for when AddToCart icon should be shown
  // const showAddToCart = ['PAX001', 'CAR', '4WD'].includes(code)
  const showAddToCart = true
  // const { setModalBody } = useModal()

  return (
    <li
      className={cn({ 'opacity-50': disabled }, 'w-80 cursor-pointer', {
        'bg-secondary-very-light order-first': withQuantity,
      })}
    >
      <div className="text-primary-base block w-full rounded-lg p-2.5 text-start shadow-sm" onClick={handleCardClick}>
        <dl className="flex h-20 w-full">
          <div className="flex h-full grow items-center">
            <dt className="sr-only">{getTranslation('termCategory')}</dt>
            <dd className="ms-2.5 me-5 shrink-0">{getIcon(code)}</dd>
            <div className="max-w-32">
              <dd className="text-small">{title}</dd>
              {oneLineInfo && <dd className="font-light uppercase">{oneLineInfo}</dd>}
            </div>
          </div>

          <div className="flex h-full min-w-28 flex-col items-end justify-between">
            <dt className="sr-only">{getTranslation('confirmationPrice')}</dt>
            <div className="me-2.5">
              {showAddToCart && !withQuantity ? <AddToCartIcon /> : withQuantity && <PenIcon width={24} />}
            </div>
            <dd className="text-lg font-bold">{formatMoney(price)}</dd>
          </div>
        </dl>

        <div className="flex items-center text-xs">
          <div className="sm:inline-flex sm:shrink-0 sm:items-center sm:gap-2">
            {withQuantity && (
              <button
                onClick={handleDec}
                className="bg-adm-blue-600 hover:bg-adm-blue-500 m-2 flex size-10 items-center justify-center rounded-full p-4 font-bold text-white"
              >
                -
              </button>
            )}
          </div>

          <div className="sm:inline-flex sm:shrink-0 sm:items-center sm:gap-2">{quantity}</div>

          <div className="sm:inline-flex sm:shrink-0 sm:items-center sm:gap-2">
            {withQuantity && (
              <button
                onClick={handleInc}
                className="bg-adm-blue-600 hover:bg-adm-blue-500 m-2 flex size-10 items-center justify-center rounded-full p-4 font-bold text-white"
              >
                +
              </button>
            )}
          </div>

          <div className="sm:inline-flex sm:shrink-0 sm:items-center sm:gap-2">
            {handleRemove && (
              <button
                onClick={handleRemove}
                className="bg-adm-red-500 hover:bg-adm-red-800 m-1 flex size-8 items-center justify-center rounded-full p-4 font-bold text-white"
              >
                x
              </button>
            )}
          </div>
        </div>
      </div>
    </li>
  )
}
